import { useEffect, useState } from "react";
import { ReactLenis } from "lenis/react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { Check, Loader2 } from "lucide-react";
import { toast, Toaster } from "sonner";
import { Nav } from "../components/site/Nav";
import { Footer } from "../components/site/Footer";
import { FloatingWhatsApp } from "../components/site/FloatingWhatsApp";
import { MENUS, MENUS_ES } from "../data/menus";
import { CONTACT } from "../data/assets";
import { fadeUp } from "../lib/motion";
import { useLanguage } from "../lib/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const empty = { name: "", email: "", phone: "", event_date: "", guests: "", event_type: "", location: "", menu: "", dietary: "", additional_info: "", type: "dining" };

export default function RequestQuote() {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const { t, lang } = useLanguage();
  const menuNames = (lang === "es" ? MENUS_ES : MENUS).map((m) => m.name);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    document.title = "Request a Quote | Mediterranean Palate";
    window.scrollTo(0, 0);
    return () => { document.title = "Private Chef in Rutland & Stamford | Mediterranean Palate"; };
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) { toast.error(t("toast.needName")); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/bookings`, form);
      setDone(true);
      toast.success(t("toast.okQuote"));
    } catch (err) { console.error(err); toast.error(t("toast.err")); }
    finally { setLoading(false); }
  };

  return (
    <ReactLenis root options={{ lerp: 0.08, smoothWheel: true }}>
      <div className="grain-overlay" />
      <Toaster theme="dark" position="bottom-right" />
      <Nav />
      <main data-testid="request-quote-page">
        <section className="tiled relative pt-28 md:pt-32 pb-10 md:pb-12 min-h-screen bg-[--bg]">
          <div className="max-w-[1600px] mx-auto px-5 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
            <motion.div variants={fadeUp} initial="hidden" animate="visible" className="lg:col-span-5 bg-[--surface] border border-white/10 rounded-2xl p-8 md:p-10 h-fit">
              <p className="font-mono uppercase tracking-[0.3em] text-[11px] text-[--gold] mb-6">{t("quote.eyebrow")}</p>
              <h1 className="font-display font-light tracking-tight text-4xl md:text-5xl text-[--text] leading-tight mb-6">{t("quote.h1a")} <span className="italic text-[--gold]">{t("quote.h1b")}</span></h1>
              <p className="text-[--text-2] text-base leading-relaxed mb-8">{t("quote.intro")}</p>
              <div className="space-y-3 font-mono text-[11px] uppercase tracking-[0.15em] text-[--text-2]">
                <p>mediterraneanpalate@outlook.com</p>
                <p>WhatsApp: {CONTACT.phoneDisplay}</p>
                <p>Rutland · Stamford · East Midlands</p>
              </div>
            </motion.div>

            <motion.div variants={fadeUp} initial="hidden" animate="visible" className="lg:col-span-6 lg:col-start-7">
              <AnimatePresence mode="wait">
                {done ? (
                  <motion.div key="s" data-testid="contact-success" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="border border-white/10 rounded-2xl p-12 bg-[--surface] flex flex-col items-start gap-6">
                    <div className="w-14 h-14 rounded-full bg-[--gold] flex items-center justify-center"><Check className="text-[#14180E]" strokeWidth={1.5} /></div>
                    <h3 className="font-display text-3xl text-[--text]">{t("quote.successH")}</h3>
                    <p className="text-[--text-2] max-w-md">{t("quote.thanks")} {form.name.split(" ")[0]}. {t("quote.successP")}</p>
                    <button data-testid="contact-reset" onClick={() => { setForm(empty); setDone(false); }} className="font-mono text-[11px] uppercase tracking-[0.2em] text-[--gold] nav-link">{t("quote.successBtn")}</button>
                  </motion.div>
                ) : (
                  <motion.form key="f" data-testid="contact-form" onSubmit={submit} exit={{ opacity: 0 }} className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-7 bg-[--surface] border border-white/10 rounded-2xl p-8 md:p-10">
                    <Field label={t("quote.l.name")}><input data-testid="c-name" className="editorial-input mt-2" value={form.name} onChange={set("name")} placeholder={t("quote.p.name")} /></Field>
                    <Field label={t("quote.l.email")}><input data-testid="c-email" type="email" className="editorial-input mt-2" value={form.email} onChange={set("email")} placeholder={t("quote.p.email")} /></Field>
                    <Field label={t("quote.l.phone")}><input data-testid="c-phone" className="editorial-input mt-2" value={form.phone} onChange={set("phone")} placeholder={t("quote.p.phone")} /></Field>
                    <Field label={t("quote.l.date")}><input data-testid="c-date" type="date" className="editorial-input mt-2" value={form.event_date} onChange={set("event_date")} /></Field>
                    <Field label={t("quote.l.guests")}><input data-testid="c-guests" className="editorial-input mt-2" value={form.guests} onChange={set("guests")} placeholder={t("quote.p.guests")} /></Field>
                    <Field label={t("quote.l.occasion")}><input data-testid="c-occasion" className="editorial-input mt-2" value={form.event_type} onChange={set("event_type")} placeholder={t("quote.p.occasion")} /></Field>
                    <Field label={t("quote.l.location")}><input data-testid="c-location" className="editorial-input mt-2" value={form.location} onChange={set("location")} placeholder={t("quote.p.location")} /></Field>
                    <Field label={t("quote.l.menu")}>
                      <select data-testid="c-menu" className="editorial-input mt-2 cursor-pointer" value={form.menu} onChange={set("menu")}>
                        <option value="" className="bg-[--surface]">{t("quote.p.menuDefault")}</option>
                        {menuNames.map((m) => <option key={m} value={m} className="bg-[--surface]">{m}</option>)}
                      </select>
                    </Field>
                    <Field label={t("quote.l.dietary")} full><input data-testid="c-dietary" className="editorial-input mt-2" value={form.dietary} onChange={set("dietary")} placeholder={t("quote.p.dietary")} /></Field>
                    <Field label={t("quote.l.info")} full><textarea data-testid="c-info" rows={3} className="editorial-input mt-2 resize-none" value={form.additional_info} onChange={set("additional_info")} placeholder={t("quote.p.info")} /></Field>
                    <div className="sm:col-span-2 mt-1">
                      <button data-testid="contact-submit" type="submit" disabled={loading} className="w-full sm:w-auto inline-flex items-center justify-center gap-3 px-10 py-4 rounded-full bg-[--gold] text-[#14180E] font-mono text-[11px] uppercase tracking-[0.16em] hover:bg-[--text] transition-colors duration-400 disabled:opacity-60">
                        {loading ? <Loader2 className="animate-spin" size={16} /> : null}{loading ? t("quote.sending") : t("quote.submit")}
                      </button>
                    </div>
                  </motion.form>
                )}
              </AnimatePresence>
            </motion.div>
          </div>
        </section>
      </main>
      <Footer />
      <FloatingWhatsApp />
    </ReactLenis>
  );
}

const Field = ({ label, children, full }) => (
  <div className={full ? "sm:col-span-2" : "sm:col-span-1"}>
    <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-[--text-2]">{label}</label>
    {children}
  </div>
);
